// base.js
// Empty application placeholder

import React, { useState, useRef } from 'react';
import { Box, Button, IconButton, Stack, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress, ListItemIcon, Breadcrumbs, TextField } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../services/api';
import * as mammoth from 'mammoth'; // Assure-toi que mammoth est installé (npm i mammoth)
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Configuration simplifiée pour Quill 2.0
const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ]
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link', 'image'
];

export default function WordApp() {
  // Ouvre automatiquement un fichier Word sélectionné via FileExplorer
  React.useEffect(() => {
    const filePath = window.localStorage.getItem('notepadApp_filePath');
    if (filePath && filePath.endsWith('.docx')) {
      // Simule un objet fichier minimal pour handleExplorerSelectFile
      handleExplorerSelectFile({ name: filePath.split('/').pop(), type: 'file' });
      window.localStorage.removeItem('notepadApp_filePath');
    }
  }, []);
  // État pour l'explorateur
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');
  // État pour le document Word
  const [wordContent, setWordContent] = useState('');
  const [wordFileName, setWordFileName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Mode visionneuse uniquement : pas d'édition, pas de sauvegarde
  const textAreaRef = useRef();

  // --- Explorateur backend (identique PaintApp/NotepadApp) ---
  async function fetchExplorerFiles(path = '/') {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data.filter(f => f.type === 'folder' || (f.type === 'file' && f.name.endsWith('.docx'))));
      setExplorerPath(path);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  }

  // Ouvre un fichier Word du backend
  async function handleExplorerSelectFile(file) {
    if (file.type !== 'file') return;
    try {
      setExplorerLoading(true);
      setExplorerError('');
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      // Récupère le .docx en binaire via /download-file
      const res = await api.get(`/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const arrayBuffer = await blob.arrayBuffer();
      // Utilise mammoth pour convertir docx → html
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setWordContent(result.value);
      setWordFileName(file.name);
      setIsEditing(false); // Set to view mode after opening
      setExplorerOpen(false);
    } catch (e) {
      setExplorerError("Erreur lors de l'ouverture du fichier Word.");
    } finally {
      setExplorerLoading(false);
    }
  }

  // Navigation
  function handleExplorerNavigate(folder) {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    fetchExplorerFiles(newPath);
  }
  function handleExplorerGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    fetchExplorerFiles(newPath);
  }

  // Sauvegarde (texte brut -> backend, à adapter pour .docx roundtrip si besoin)
  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError('');
    try {
      // Nettoyer le HTML avant conversion
      const cleanHtml = wordContent.replace(/<img[^>]+src="([^">]+)"[^>]*>/g, 
        (match, src) => src.startsWith('http') ? match : '');

      const user = JSON.parse(window.localStorage.getItem('user'));
      const token = window.localStorage.getItem('token');
      const relPath = (explorerPath === '/' ? wordFileName : 
        `${explorerPath.replace(/^\//, '')}/${wordFileName}`).replace(/^\//, '');

      const formData = new FormData();
      formData.append('html', cleanHtml);
      formData.append('uuid7', user.uuid7 || user.uuid || user.id);
      formData.append('path', relPath);

      const response = await api.post('/api/convert/html-to-docx', formData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      setIsEditing(false);
    } catch (error) {
      setSaveError(`Erreur: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  // UI principale
  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip title="Ouvrir depuis l'explorateur">
          <IconButton color="primary" onClick={() => { setExplorerOpen(true); fetchExplorerFiles('/'); }}>
            <FolderOpenIcon />
          </IconButton>
        </Tooltip>

        {wordFileName && (
          <>
            <Typography variant="subtitle1" sx={{ ml: 2 }}>{wordFileName}</Typography>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                disabled={!wordFileName}
              >
                Modifier
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saveLoading}
                >
                  {saveLoading ? <CircularProgress size={24} /> : 'Enregistrer'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => setIsEditing(false)}
                  disabled={saveLoading}
                >
                  Annuler
                </Button>
              </>
            )}
          </>
        )}
      </Stack>

      {/* Zone de contenu du document Word */}
      {wordFileName && (
        <Box sx={{ mt: 2 }}>
          {saveError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {saveError}
            </Typography>
          )}
          {isEditing ? (
            <ReactQuill 
              theme="snow"
              value={wordContent}
              onChange={setWordContent}
              modules={modules}
              formats={formats}
              style={{ 
                height: 'calc(100vh - 300px)',
                width: '100%',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            />
          ) : (
            <Box sx={{ bgcolor: '#fff', border: '1px solid #ccc', borderRadius: 2, p: 3, minHeight: 200 }}>
              <div dangerouslySetInnerHTML={{ __html: wordContent }} />
            </Box>
          )}
        </Box>
      )}

      {/* Explorateur de fichiers backend */}
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ouvrir un fichier Word</DialogTitle>
        <DialogContent>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 15, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => fetchExplorerFiles('/')}>Racine</span>
              {explorerPath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => fetchExplorerFiles(path)}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
          </Box>
          <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 270, overflowY: 'auto', mt: 1, mb: 2 }}>
            {explorerLoading ? <CircularProgress /> : (
              <List sx={{ fontSize: 17 }}>
                {explorerFiles.map((file, idx) => (
                  <ListItem
                    button={file.type === 'folder' || (file.type === 'file' && file.name.endsWith('.docx'))}
                    key={idx}
                    onDoubleClick={file.type === 'folder' ? () => handleExplorerNavigate(file.name) : undefined}
                    onClick={file.type === 'file' && file.name.endsWith('.docx') ? () => handleExplorerSelectFile(file) : undefined}
                    sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: file.type === 'folder' ? '#e3f2fd' : undefined } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontSize: 17, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222' }}>{file.name}</span>}
                      secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                      style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                    />
                  </ListItem>
                ))}
                {(!explorerFiles || explorerFiles.length === 0) && <ListItem><ListItemText primary={<span style={{ fontSize: 16 }}>Dossier vide</span>} /></ListItem>}
              </List>
            )}
            {explorerError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{explorerError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)} size="large">Annuler</Button>
        </DialogActions>
      </Dialog>

      {/* Zone de visionnage Word */}
      {wordContent && (
        <Box sx={{ mt: 3 }}>
        </Box>
      )}
    </Box>
  );
}